import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Wrench, AlertTriangle, Clock, CheckCircle2, Euro, Plus, ChevronRight,
  Search, Filter, X as XIcon, LayoutGrid, ListChecks, CalendarClock, Package,
  Truck, BarChart3, Zap, Snowflake, Utensils, Cpu, Boxes, MoreHorizontal,
  ExternalLink, ArrowUp, ArrowDown, Loader2, MapPin,
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import SupportTicketsInbox from "@/components/support/SupportTicketsInbox";

import { OrbitAI } from "@/components/OrbitAI";

const CLR_FAM = {
  Froid: "#0EA5E9", Chaud: "#EF4444", Cuisine: "#F59E0B", Eau: "#06B6D4",
  Electricite: "#8B5CF6", IT: "#10B981", Mobilier: "#A78BFA", Securite: "#DC2626", Autres: "#94A3B8",
};
const ICON_FAM = { Froid: Snowflake, Chaud: Zap, Cuisine: Utensils, Eau: Boxes, Electricite: Zap, IT: Cpu, Mobilier: Package, Securite: AlertTriangle, Autres: Boxes };
const CAT_LABELS = { Froid:"Froid", Chaud:"Chaud", Cuisine:"Cuisine", Eau:"Eau", Electricite:"Électricité", IT:"IT", Mobilier:"Mobilier", Securite:"Sécurité", Autres:"Autres" };
const CATEGORIES_LIST = ["Froid","Chaud","Cuisine","Eau","Electricite","IT","Mobilier","Securite","Autres"];
const EQ_STATUT = {
  ok:             { label: "En service",     cls: "bg-emerald-100 text-emerald-700" },
  a_surveiller:   { label: "À surveiller",   cls: "bg-amber-100 text-amber-700" },
  en_maintenance: { label: "En maintenance", cls: "bg-blue-100 text-blue-700" },
  en_panne:       { label: "Hors service",   cls: "bg-rose-100 text-rose-700" },
  hors_service:   { label: "Hors service",   cls: "bg-rose-100 text-rose-700" },
  reforme:        { label: "Réformé",        cls: "bg-slate-200 text-slate-600" },
};
const PRIO_STYLE = {
  P1: "bg-rose-100 text-rose-700 ring-1 ring-rose-200",
  P2: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  P3: "bg-blue-100 text-blue-700 ring-1 ring-blue-200",
  P4: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
};
const STAT_STYLE = {
  nouveau:      { cls: "bg-blue-100 text-blue-700",       label: "Nouveau" },
  a_qualifier:  { cls: "bg-indigo-100 text-indigo-700",   label: "À qualifier" },
  affecte:      { cls: "bg-purple-100 text-purple-700",   label: "Affecté" },
  planifie:     { cls: "bg-cyan-100 text-cyan-700",       label: "Planifié" },
  en_cours:     { cls: "bg-blue-100 text-blue-700",       label: "En cours" },
  en_attente:   { cls: "bg-amber-100 text-amber-700",     label: "En attente" },
  resolu:       { cls: "bg-emerald-100 text-emerald-700", label: "Résolu" },
  cloture:      { cls: "bg-slate-100 text-slate-600",     label: "Clôturé" },
  // legacy aliases
  ouverte:      { cls: "bg-blue-100 text-blue-700",       label: "Ouverte" },
  resolue:      { cls: "bg-emerald-100 text-emerald-700", label: "Résolue" },
  cloturee:     { cls: "bg-slate-100 text-slate-600",     label: "Clôturée" },
};
const PRIO_LABEL = { P1: "P1 Critique", P2: "P2 Urgent", P3: "P3 Standard", P4: "P4 Amélioration" };
const ZONES = ["Corner", "Corner extérieur", "Réserve", "Cuisine", "Salle", "Chambre froide", "Vitrine", "Extérieur", "Autre"];
const CATEGORIES_FALLBACK = ["Panne totale", "Dysfonctionnement", "Fuite", "Bruit anormal", "Température hors seuil", "Équipement cassé", "Autre"];
const IMPACTS = [
  { k: "bloquant",  label: "Bloquant", sub: "Activité impossible", tone: "rose",    prio: "P1" },
  { k: "important", label: "Important", sub: "Activité dégradée",  tone: "amber",   prio: "P2" },
  { k: "mineur",    label: "Mineur",   sub: "Gêne sans blocage",   tone: "slate",   prio: "P3" },
];
const TIMELINE_STEPS = [
  { k: "signalement", label: "Signalement" },
  { k: "intervention", label: "Intervention" },
  { k: "cloture", label: "Clôture" },
];
const NIV_STYLE = {
  critique:   { dot: "bg-rose-500",    txt: "text-rose-700",    label: "Critique" },
  surveiller: { dot: "bg-amber-500",   txt: "text-amber-700",   label: "À surveiller" },
  conforme:   { dot: "bg-emerald-500", txt: "text-emerald-700", label: "Conforme" },
  inactif:    { dot: "bg-slate-300",   txt: "text-slate-500",   label: "Inactif" },
};

const fmtEur = (v) => v == null ? "—" : `${Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g," ")} €`;
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); } catch { return "—"; } };
const fmtDaysAgo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso); const now = new Date();
  const h = Math.floor((now - d) / 3600000);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h/24)}j`;
};

const TABS = [
  { k: "cockpit",     label: "Cockpit",              icon: LayoutGrid },
  { k: "interventions", label: "Interventions",      icon: ListChecks },
  { k: "preventif",   label: "Maintenance préventive", icon: CalendarClock },
  { k: "parc",        label: "Parc & équipements",   icon: Package },
  { k: "prestataires", label: "Prestataires",        icon: Truck },
  { k: "analyse",     label: "Analyse & rapports",   icon: BarChart3 },
];

export default function Maintenance() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "cockpit";
  const setTab = (k) => setSp({ tab: k });
  const [showSignaler, setShowSignaler] = useState(false);
  // Filtres globaux Région / DR / Secteur / Corner / Année / Mois
  const [gf, setGf] = useState({ region: "", dr: "", secteur: "", corner_key: "", annee: "", mois: "" });
  const [opts, setOpts] = useState({ regions: [], drs: [], secteurs: [], corners: [], annees: [] });
  useEffect(() => { api.get("/maintenance/filter-options").then(r => setOpts(r.data)).catch(() => {}); }, []);
  const openSignaler = () => setShowSignaler(true);

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="p-4 md:p-6">
        <Header onSignaler={openSignaler}/>
        <div className="mt-4 flex items-center gap-1 border-b border-slate-200 overflow-x-auto scrollbar-thin">
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`maint-tab-${t.k}`}
                    className={`flex items-center gap-2 px-3 py-2 text-[12px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                      tab === t.k
                        ? "border-blue-600 text-blue-700"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>
              <t.icon className="w-3.5 h-3.5"/> {t.label}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <SupportTicketsInbox service="Maintenance" testid="maint-tickets-inbox"/>
        </div>
        <div className="mt-3">
          <GlobalFilterBar gf={gf} setGf={setGf} opts={opts}/>
        </div>
        <div className="mt-3">
          {tab === "cockpit"      && <Cockpit gf={gf} onSignaler={openSignaler} setTab={setTab}/>}
          {tab === "interventions"&& <Interventions gf={gf} onSignaler={openSignaler}/>}
          {tab === "preventif"    && <Preventif gf={gf}/>}
          {tab === "parc"         && <Parc gf={gf}/>}
          {tab === "prestataires" && <Prestataires gf={gf}/>}
          {tab === "analyse"      && <Analyse gf={gf}/>}
        </div>
      </div>
      {showSignaler && <SignalerModal onClose={() => setShowSignaler(false)}/>}
    </div>
  );
}

/* ─── FILTER BAR GLOBAL — cascadée sur données réelles ───────────────── */
function GlobalFilterBar({ gf, setGf, opts }) {
  // Cascades : région → DR → secteur → corner
  const drsForRegion = useMemo(() => {
    if (!gf.region) return opts.drs || [];
    const rs = gf.region.split(",");
    return [...new Set((opts.corners || []).filter(c => rs.includes(c.region)).map(c => c.dr).filter(Boolean))].sort();
  }, [gf.region, opts]);
  const secteursForDr = useMemo(() => {
    const base = (opts.corners || []).filter(c =>
      (!gf.region || gf.region.split(",").includes(c.region)) && (!gf.dr || gf.dr.split(",").includes(c.dr))
    );
    return [...new Set(base.map(c => c.secteur).filter(Boolean))].sort();
  }, [gf.region, gf.dr, opts]);
  const cornersForFilter = useMemo(() => {
    return (opts.corners || []).filter(c =>
      (!gf.region || gf.region.split(",").includes(c.region)) &&
      (!gf.dr || gf.dr.split(",").includes(c.dr)) &&
      (!gf.secteur || gf.secteur.split(",").includes(c.secteur))
    );
  }, [gf.region, gf.dr, gf.secteur, opts]);
  // Années distinctes de maintenance_interventions
  const years = useMemo(() => {
    const arr = [{v: "", l: "Toutes années"}];
    (opts.annees || []).forEach(y => arr.push({v: String(y), l: String(y)}));
    return arr;
  }, [opts.annees]);
  // Mois — visibles seulement si année choisie
  const months = [
    {v: "", l: "Tous mois"}, {v:"01",l:"Janvier"}, {v:"02",l:"Février"}, {v:"03",l:"Mars"},
    {v:"04",l:"Avril"}, {v:"05",l:"Mai"}, {v:"06",l:"Juin"},
    {v:"07",l:"Juillet"}, {v:"08",l:"Août"}, {v:"09",l:"Septembre"},
    {v:"10",l:"Octobre"}, {v:"11",l:"Novembre"}, {v:"12",l:"Décembre"},
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-2.5 flex items-center gap-2 flex-wrap" data-testid="maint-filterbar">
      <Filter className="w-3.5 h-3.5 text-slate-400 ml-1"/>
      <FilterSelect label="Région" value={gf.region} setValue={v => setGf({...gf, region: v, dr: "", secteur: "", corner_key: ""})}
                     options={[{v:"", l:"Toutes régions"}, ...(opts.regions || []).map(x => ({v:x, l:x}))]}/>
      <FilterSelect label="DR" value={gf.dr} setValue={v => setGf({...gf, dr: v, secteur: "", corner_key: ""})}
                     options={[{v:"", l:"Tous DR"}, ...drsForRegion.map(x => ({v:x, l:x}))]}/>
      <FilterSelect label="Secteur" value={gf.secteur} setValue={v => setGf({...gf, secteur: v, corner_key: ""})}
                     options={[{v:"", l:"Tous secteurs"}, ...secteursForDr.map(x => ({v:x, l:x}))]}/>
      <FilterSelect label="Corner" value={gf.corner_key} setValue={v => setGf({...gf, corner_key: v})}
                     options={[{v:"", l:"Tous corners"}, ...cornersForFilter.slice(0, 500).map(c => ({v:c.corner_key, l:c.corner}))]}/>
      <FilterSelect label="Année" value={gf.annee} setValue={v => setGf({...gf, annee: v, mois: ""})} options={years}/>
      <FilterSelect label="Mois" value={gf.mois} setValue={v => setGf({...gf, mois: v})}
                     options={gf.annee ? months : [{v:"", l:"Sélectionne d'abord une année"}]}/>
      {(gf.region || gf.dr || gf.secteur || gf.corner_key || gf.annee || gf.mois) && (
        <button onClick={() => setGf({region:"",dr:"",secteur:"",corner_key:"",annee:"",mois:""})}
                data-testid="filterbar-reset"
                className="ml-1 text-[10px] font-semibold text-slate-500 hover:text-rose-600 flex items-center gap-1">
          <XIcon className="w-3 h-3"/> Réinitialiser
        </button>
      )}
    </div>
  );
}

/* ─── HEADER ──────────────────────────────────────────────────────────── */
function Header({ onSignaler }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Wrench className="w-6 h-6 text-blue-600"/> Cockpit Maintenance
        </h1>
        <p className="text-[12px] text-slate-500 mt-0.5">Vue d'ensemble de l'état de votre parc et des interventions</p>
      </div>
      <button onClick={onSignaler} data-testid="maint-signaler-btn"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold px-4 py-2 rounded-full shadow-md shadow-blue-900/20 transition-colors">
        <Plus className="w-4 h-4"/> Signaler un problème
      </button>
    </div>
  );
}

/* ─── COCKPIT ─────────────────────────────────────────────────────────── */
function Cockpit({ gf, onSignaler, setTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ famille: "", priorite: "", statut: "" });
  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries({...f, ...gf}).filter(([, v]) => v));
    api.get("/maintenance/cockpit", { params })
       .then(r => setData(r.data))
       .catch(() => toast.error("Impossible de charger le cockpit"))
       .finally(() => setLoading(false));
  }, [f, gf]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="p-12 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  if (!data) return null;

  const kpiCards = [
    { k: "ouvertes",  label: "Interventions ouvertes",   value: data.kpis.ouvertes.value,  sub: data.kpis.ouvertes.sub,  icon: ListChecks, tone: "info" },
    { k: "critiques", label: "Interventions critiques",  value: data.kpis.critiques.value, sub: data.kpis.critiques.sub, icon: AlertTriangle, tone: "critical" },
    { k: "retard",    label: "En retard",                value: data.kpis.en_retard.value, sub: data.kpis.en_retard.sub, icon: Clock, tone: "warning" },
    { k: "equip",     label: "Équipements indisponibles",value: data.kpis.equip_ko.value,  sub: data.kpis.equip_ko.sub,  icon: CheckCircle2, tone: "warning" },
    { k: "cout",      label: "Coûts maintenance du mois",value: fmtEur(data.kpis.cout_mois.value), sub: data.kpis.cout_mois.sub, icon: Euro, tone: "info" },
  ];

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-slate-400"/>
        <FilterSelect label="Famille" value={f.famille} setValue={v => setF({...f, famille: v})}
                       options={[{v:"", l:"Toutes familles"}, {v:"Froid", l:"Froid"}, {v:"Cuisine", l:"Cuisine"}, {v:"Electricite", l:"Électricité"}, {v:"IT", l:"IT"}, {v:"Autres", l:"Autres"}]}/>
        <FilterSelect label="Priorité" value={f.priorite} setValue={v => setF({...f, priorite: v})}
                       options={[{v:"", l:"Toutes priorités"}, {v:"P1", l:"P1"}, {v:"P2", l:"P2"}, {v:"P3", l:"P3"}, {v:"P4", l:"P4"}]}/>
        <FilterSelect label="Statut" value={f.statut} setValue={v => setF({...f, statut: v})}
                       options={[{v:"", l:"Tous statuts"}, {v:"ouverte", l:"Ouverte"}, {v:"en_cours", l:"En cours"}, {v:"en_attente", l:"En attente"}, {v:"resolue", l:"Résolue"}, {v:"cloturee", l:"Clôturée"}]}/>
        <div className="ml-auto text-[11px] text-slate-500">
          {data.meta.total_interventions} interventions · {data.meta.total_equipements} équipements
        </div>
      </div>

      {/* 5 KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpiCards.map(k => <KpiCard key={k.k} {...k}/>)}
      </div>

      {/* Row 2 : À traiter maintenant + État corners + Prochaines maintenances */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-5">
          <ATraiterTable rows={data.a_traiter}/>
        </div>
        <div className="lg:col-span-4">
          <EtatCornersTable rows={data.etat_corners}/>
        </div>
        <div className="lg:col-span-3">
          <ProchainesMaintenances data={data.prochaines}/>
        </div>
      </div>

      {/* Row 3 : 3 graphs + signaux */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-4"><InterventionsChart data={data.graphs.hist30}/></div>
        <div className="lg:col-span-3"><TopProblemsChart data={data.graphs.top_problem}/></div>
        <div className="lg:col-span-3"><CostsPieChart data={data.graphs.costs_pie}/></div>
        <div className="lg:col-span-2"><SignauxCard signaux={data.signaux}/></div>
      </div>

      {/* Actions rapides */}
      <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-slate-200 -mx-4 md:-mx-6 px-4 md:px-6 py-3 flex flex-wrap items-center gap-2 shadow-lg">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-2">Actions rapides</span>
        <ActionBtn onClick={onSignaler} icon={Plus} label="Nouvelle intervention" primary testid="qa-nouvelle-inter"/>
        <ActionBtn onClick={() => setTab("preventif")} icon={CalendarClock} label="Maintenance préventive" testid="qa-preventif"/>
        <ActionBtn onClick={() => setTab("parc")} icon={Package} label="Parc & équipements" testid="qa-parc"/>
        <ActionBtn onClick={() => setTab("prestataires")} icon={Truck} label="Prestataires" testid="qa-prest"/>
        <ActionBtn onClick={() => setTab("analyse")} icon={BarChart3} label="Analyse & rapports" testid="qa-analyse"/>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone }) {
  const toneCls = {
    critical: "bg-rose-50 text-rose-700",
    warning:  "bg-amber-50 text-amber-700",
    info:     "bg-blue-50 text-blue-700",
    success:  "bg-emerald-50 text-emerald-700",
  }[tone] || "bg-slate-50 text-slate-700";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
      <div className="flex items-center gap-2">
        <div className={`w-9 h-9 rounded-lg grid place-items-center ${toneCls}`}><Icon className="w-4 h-4"/></div>
        <div className="text-[10px] uppercase font-bold text-slate-500 leading-tight">{label}</div>
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function ATraiterTable({ rows }) {
  const critical = rows.filter(r => r.priorite === "P1").length;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[13px] font-bold text-slate-900">À traiter maintenant</h3>
        {critical > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{critical} critique{critical>1?"s":""}</span>}
      </div>
      <div className="max-h-[340px] overflow-y-auto scrollbar-thin">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr className="text-left uppercase text-[9px] tracking-wider text-slate-500 font-bold">
              <th className="px-3 py-2">Prio</th>
              <th className="px-2 py-2">Corner</th>
              <th className="px-2 py-2">Équipement</th>
              <th className="px-2 py-2">Incident</th>
              <th className="px-2 py-2 text-right">Depuis</th>
              <th className="px-2 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(i => (
              <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIO_STYLE[i.priorite]}`}>{i.priorite}</span></td>
                <td className="px-2 py-2 font-semibold text-slate-900 whitespace-nowrap truncate max-w-[100px]">{i.corner || "—"}</td>
                <td className="px-2 py-2 text-slate-600 whitespace-nowrap truncate max-w-[110px]">{i.equipement_label || "—"}</td>
                <td className="px-2 py-2 text-slate-700 whitespace-nowrap truncate max-w-[120px]">{i.titre}</td>
                <td className="px-2 py-2 text-right tabular-nums text-slate-500">{fmtDaysAgo(i.date_creation)}</td>
                <td className="px-2 py-2"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${STAT_STYLE[i.statut]?.cls}`}>{STAT_STYLE[i.statut]?.label}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">Aucune intervention à traiter — bien joué !</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EtatCornersTable({ rows }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[13px] font-bold text-slate-900">État du réseau</h3>
        <div className="flex items-center gap-2 text-[9px]">
          {Object.entries(NIV_STYLE).map(([k, s]) => (
            <span key={k} className="flex items-center gap-1 text-slate-600"><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}/>{s.label}</span>
          ))}
        </div>
      </div>
      <div className="max-h-[340px] overflow-y-auto scrollbar-thin">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr className="text-left uppercase text-[9px] tracking-wider text-slate-500 font-bold">
              <th className="px-3 py-2">Corner</th>
              <th className="px-2 py-2">Région</th>
              <th className="px-2 py-2 text-right">Ouv.</th>
              <th className="px-2 py-2 text-right">KO</th>
              <th className="px-2 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => {
              const s = NIV_STYLE[c.niveau] || NIV_STYLE.inactif;
              return (
                <tr key={c.corner_key} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5"><div className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}/><span className="font-semibold text-slate-900 truncate max-w-[100px]">{c.corner || "—"}</span></div></td>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap truncate max-w-[80px]">{c.region || c.dr || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{c.n_ouvertes}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${c.n_ko>0?"text-rose-700":"text-slate-400"}`}>{c.n_ko}</td>
                  <td className={`px-2 py-1.5 text-[9px] font-semibold ${s.txt}`}>{s.label}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400 italic">Aucun corner</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProchainesMaintenances({ data }) {
  const sections = [
    { k: "en_retard", label: "En retard",       tone: "rose",    items: data.en_retard },
    { k: "dans_7j",   label: "Dans les 7 jours",tone: "amber",   items: data.dans_7j },
    { k: "dans_30j",  label: "Dans les 30 jours",tone: "blue",  items: data.dans_30j },
  ];
  const toneCls = { rose: "text-rose-700 bg-rose-50", amber: "text-amber-700 bg-amber-50", blue: "text-blue-700 bg-blue-50" };
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-[13px] font-bold text-slate-900">Prochaines maintenances</h3>
      </div>
      <div className="max-h-[340px] overflow-y-auto scrollbar-thin divide-y divide-slate-100">
        {sections.map(s => (
          <div key={s.k} className="px-3 py-2">
            <div className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${toneCls[s.tone]}`}>{s.label} ({s.items.length})</div>
            <div className="mt-1.5 space-y-1">
              {s.items.slice(0, 4).map(p => (
                <div key={p.id} className="flex items-center gap-2 text-[11px]">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-slate-800">{p.equipement_label || p.intitule}</div>
                    <div className="text-[10px] text-slate-500 truncate">{p.corner}</div>
                  </div>
                  <div className="tabular-nums text-[10px] text-slate-600 whitespace-nowrap">{fmtDate(p.prochaine_echeance)}</div>
                </div>
              ))}
              {s.items.length === 0 && <div className="text-[10px] text-slate-400 italic">Aucun</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InterventionsChart({ data }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[13px] font-bold text-slate-900">Interventions dans le temps</h3>
        <span className="text-[10px] text-slate-500">30 derniers jours</span>
      </div>
      <div className="h-40">
        <ResponsiveContainer>
          <LineChart data={data} margin={{top:6,right:8,left:0,bottom:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="label" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} interval={5}/>
            <YAxis allowDecimals={false} tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} width={22}/>
            <Tooltip contentStyle={{fontSize:11}}/>
            <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2} dot={{r:2}} activeDot={{r:4}}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TopProblemsChart({ data }) {
  const bars = data.map(d => ({ ...d, label: (d.label || "").slice(0, 22) }));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-[13px] font-bold text-slate-900 mb-2">Top 5 équipements problématiques</h3>
      <div className="h-40">
        <ResponsiveContainer>
          <BarChart data={bars} layout="vertical" margin={{top:0,right:12,left:0,bottom:0}}>
            <CartesianGrid horizontal={false} stroke="#f1f5f9"/>
            <XAxis type="number" allowDecimals={false} tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false}/>
            <YAxis type="category" dataKey="label" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} width={110}/>
            <Tooltip contentStyle={{fontSize:11}}/>
            <Bar dataKey="n" fill="#F59E0B" radius={[0,4,4,0]}>
              {bars.map((b, i) => <Cell key={i} fill={CLR_FAM[b.famille] || "#94A3B8"}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CostsPieChart({ data }) {
  const total = data.reduce((a, x) => a + (x.cout || 0), 0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-[13px] font-bold text-slate-900 mb-2">Coûts maintenance <span className="text-[10px] font-normal text-slate-500">· Ce mois</span></h3>
      <div className="grid grid-cols-2 gap-2 items-center">
        <div className="h-32 relative">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} dataKey="cout" innerRadius={30} outerRadius={50} paddingAngle={2}>
                {data.map((d, i) => <Cell key={i} fill={CLR_FAM[d.famille] || "#94A3B8"}/>)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center">
              <div className="text-sm font-bold text-slate-900 tabular-nums">{fmtEur(total)}</div>
              <div className="text-[9px] text-slate-500">Total</div>
            </div>
          </div>
        </div>
        <div className="space-y-1">
          {data.map(d => (
            <div key={d.famille} className="flex items-center gap-1.5 text-[10px]">
              <span className="w-2 h-2 rounded-full" style={{background: CLR_FAM[d.famille] || "#94A3B8"}}/>
              <span className="flex-1 truncate text-slate-700">{d.famille}</span>
              <span className="tabular-nums font-semibold text-slate-900">{fmtEur(d.cout)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignauxCard({ signaux }) {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4">
      <div className="flex items-center gap-2 mb-2">
        <OrbitAI className="w-4 h-4 text-purple-600"/>
        <h3 className="text-[12px] font-bold text-slate-900">À surveiller par Orbit</h3>
      </div>
      <div className="text-[9px] italic text-slate-500 mb-2">Signaux à investiguer (non diagnostics)</div>
      <div className="space-y-2">
        {signaux.map((s, i) => (
          <div key={i} className={`p-2 rounded-lg bg-white border ${s.severity === "critique" ? "border-rose-200" : "border-amber-200"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Signal : {s.type.replace(/_/g, " ")}</div>
            <div className="text-[11px] font-semibold text-slate-900 mt-0.5 leading-tight">{s.label}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{s.comparison}</div>
          </div>
        ))}
        {signaux.length === 0 && <div className="text-[10px] text-slate-400 italic p-2">Aucun signal détecté</div>}
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, primary, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              primary ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}>
      <Icon className="w-3.5 h-3.5"/> {label}
    </button>
  );
}

function FilterSelect({ label, value, setValue, options }) {
  const all = (options || []).find(o => o.v === "");
  return (
    <FilterMultiSelect label={label} testid={`maint-filter-${label.toLowerCase()}`} size="sm" menuWidth={230}
                       placeholder={all?.l || `Tous · ${label}`}
                       options={(options || []).filter(o => o.v !== "").map(o => ({ value: o.v, label: o.l }))}
                       value={(value || "").split(",").filter(Boolean)}
                       onChange={arr => setValue(arr.join(","))}/>
  );
}

/* ─── INTERVENTIONS TAB ───────────────────────────────────────────────── */
function Interventions({ gf, onSignaler }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const [f, setF] = useState({ statut: "", priorite: "" });
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries({...f, ...gf}).filter(([, v]) => v));
    api.get("/maintenance/interventions", { params }).then(r => setRows(r.data || []));
  }, [f, gf]);
  useEffect(() => { load(); }, [load]);
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(r => `${r.numero||""} ${r.corner||""} ${r.titre||""} ${r.equipement_label||""} ${r.categorie||""}`.toLowerCase().includes(qq));
  }, [rows, q]);
  return (
    <>
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <h2 className="text-[13px] font-bold text-slate-900">Interventions</h2>
        <span className="text-[11px] text-slate-500">{filtered.length}</span>
        <button onClick={onSignaler} data-testid="int-nouvelle-btn"
                className="ml-2 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg shadow-sm">
          <Plus className="w-3.5 h-3.5"/> Nouvelle intervention
        </button>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                 data-testid="maint-int-search"
                 className="pl-8 pr-3 py-1.5 rounded-full border border-slate-200 text-xs w-56 outline-none focus:ring-2 focus:ring-blue-100"/>
        </div>
        <FilterSelect label="Statut" value={f.statut} setValue={v => setF({...f, statut: v})}
                       options={[{v:"", l:"Tous statuts"}, ...Object.entries(STAT_STYLE).slice(0,8).map(([k,s]) => ({v:k, l:s.label}))]}/>
        <FilterSelect label="Priorité" value={f.priorite} setValue={v => setF({...f, priorite: v})}
                       options={[{v:"", l:"Toutes priorités"}, {v:"P1", l:"P1"}, {v:"P2", l:"P2"}, {v:"P3", l:"P3"}, {v:"P4", l:"P4"}]}/>
      </div>
      <div className="overflow-x-auto max-h-[600px] scrollbar-thin">
        <table className="w-full text-[11px] min-w-[1300px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
            <tr className="text-left uppercase text-[9px] tracking-wider text-slate-500 font-bold">
              <th className="px-3 py-2">Numéro</th><th className="px-2 py-2">Prio</th>
              <th className="px-2 py-2">Corner</th><th className="px-2 py-2">Équipement</th>
              <th className="px-2 py-2">Catégorie</th><th className="px-2 py-2">Description</th>
              <th className="px-2 py-2 text-right">Ancienneté</th>
              <th className="px-2 py-2">Responsable</th><th className="px-2 py-2">Prestataire</th>
              <th className="px-2 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(i => (
              <tr key={i.id} onClick={() => setDetailId(i.id)}
                  className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer"
                  data-testid={`int-row-${i.id}`}>
                <td className="px-3 py-1.5 font-semibold tabular-nums text-slate-800 whitespace-nowrap">{i.numero || "—"}</td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIO_STYLE[i.priorite]}`}>{i.priorite}</span></td>
                <td className="px-2 py-1.5 whitespace-nowrap truncate max-w-[110px]">{i.corner || "—"}</td>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[130px]">{i.equipement_label || "—"}</td>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[100px]">{i.categorie || "—"}</td>
                <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap truncate max-w-[160px]">{i.description || i.titre}</td>
                <td className="px-2 py-1.5 text-right text-slate-500 whitespace-nowrap">{fmtDaysAgo(i.date_creation)}</td>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[110px]">{i.responsable_nom || "—"}</td>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[120px]">{i.prestataire_nom || "—"}</td>
                <td className="px-2 py-1.5"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${STAT_STYLE[i.statut]?.cls}`}>{STAT_STYLE[i.statut]?.label || i.statut}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400 italic">Aucune intervention</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
    {detailId && <InterventionDetailModal iid={detailId} onClose={() => { setDetailId(null); load(); }}/>}
    </>
  );
}

function InterventionDetailModal({ iid, onClose }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [drawer, setDrawer] = useState(null); // "intervention" | "cloture" | null
  const [prests, setPrests] = useState([]);
  const load = () => api.get(`/maintenance/interventions/${iid}`).then(r => setData(r.data));
  useEffect(() => { load(); api.get("/maintenance/prestataires").then(r => setPrests(r.data || [])); /* eslint-disable-line */ }, [iid]);
  const openStep = (step) => {
    if (step === "signalement") return; // déjà fait à la création
    setDrawer(step);
  };
  const submitStep = async (step, payload) => {
    setSaving(true);
    try {
      const r = await api.post(`/maintenance/interventions/${iid}/step`, { step, comment, payload });
      setComment(""); setDrawer(null); await load();
      toast.success(step === "cloture" ? "Intervention clôturée" : "Prise en charge enregistrée");
      if (r.data.follow_up_intervention_id) {
        toast.success("Nouvelle intervention de suivi créée automatiquement");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  if (!data) return null;
  const completed = new Set((data.timeline || []).map(t => t.step));
  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto relative" onClick={e => e.stopPropagation()} data-testid="int-detail-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="font-mono">{data.numero}</span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIO_STYLE[data.priorite]}`}>{PRIO_LABEL[data.priorite] || data.priorite}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STAT_STYLE[data.statut]?.cls}`}>{STAT_STYLE[data.statut]?.label}</span>
              {data.follow_up_numero && <span className="text-[10px] text-blue-600">→ Suivi : {data.follow_up_numero}</span>}
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-1">{data.titre}</h2>
            <p className="text-[11px] text-slate-500">{data.corner || "—"} · {data.equipement_label || "—"} · {data.categorie || "—"}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XIcon className="w-4 h-4"/></button>
        </div>

        {/* Timeline horizontale — 3 étapes */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/70">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Timeline</div>
          <div className="flex items-center gap-2">
            {TIMELINE_STEPS.map((s, idx) => {
              const done = completed.has(s.k);
              const entry = (data.timeline || []).slice().reverse().find(t => t.step === s.k);
              return (
                <div key={s.k} className="flex items-center gap-2 flex-1">
                  <button onClick={() => openStep(s.k)} disabled={saving || s.k === "signalement"}
                          data-testid={`tl-${s.k}`}
                          className={`flex-1 text-left px-3 py-2 rounded-lg border transition-colors ${
                            done ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                 : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                          } ${s.k === "signalement" ? "cursor-default" : "cursor-pointer"}`}>
                    <div className="flex items-center gap-1.5">
                      {done ? <CheckCircle2 className="w-3.5 h-3.5"/> : <Clock className="w-3.5 h-3.5"/>}
                      <span className="text-[12px] font-semibold">{s.label}</span>
                    </div>
                    {done && entry && <div className="text-[9px] text-slate-500 mt-0.5">{fmtDate(entry.at)} · {entry.by_name || "—"}</div>}
                    {!done && s.k !== "signalement" && <div className="text-[9px] text-slate-400 mt-0.5">Cliquer pour compléter</div>}
                  </button>
                  {idx < TIMELINE_STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0"/>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body — infos + commentaires */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <InfoLine label="Créée le" value={fmtDate(data.date_creation)}/>
            <InfoLine label="Créateur" value={data.created_by_name || "—"}/>
            <InfoLine label="Zone" value={data.zone || "—"}/>
            <InfoLine label="Impact" value={data.impact || "—"}/>
            <InfoLine label="Prestataire" value={data.prestataire_nom || "—"}/>
            {data.prise_par_nom && <InfoLine label="Prise en charge par" value={data.prise_par_nom}/>}
            {data.prise_quand && <InfoLine label="Prévue le" value={fmtDate(data.prise_quand)}/>}
            {data.compte_rendu && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Compte rendu</div>
                <div className="text-[12px] text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-2">{data.compte_rendu}</div>
              </div>
            )}
            {data.description && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Description</div>
                <div className="text-[12px] text-slate-700 whitespace-pre-wrap">{data.description}</div>
              </div>
            )}
            {data.photos && data.photos.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Photos</div>
                <div className="flex gap-2 flex-wrap">
                  {data.photos.filter(Boolean).map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noreferrer"><img src={p} alt="" className="w-20 h-20 object-cover rounded border border-slate-200"/></a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Historique</div>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {(data.timeline || []).slice().reverse().map((t, i) => (
                <div key={i} className="text-[11px] border-l-2 border-blue-300 pl-2">
                  <div className="font-semibold text-slate-800 capitalize">{t.step.replace(/_/g, " ")}</div>
                  <div className="text-[10px] text-slate-500">{fmtDate(t.at)} · {t.by_name || "—"}</div>
                  {t.comment && <div className="text-[11px] text-slate-600 mt-0.5 italic">"{t.comment}"</div>}
                  {t.payload && Object.keys(t.payload).length > 0 && (
                    <div className="text-[10px] text-slate-600 mt-0.5">
                      {Object.entries(t.payload).filter(([k,v]) => v && k !== "documents").map(([k,v]) =>
                        <div key={k}><span className="font-semibold">{k}</span>: {String(v)}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Drawer latéral pour Intervention */}
        {drawer === "intervention" && (
          <InterventionStepDrawer onClose={() => setDrawer(null)} onSubmit={p => submitStep("intervention", p)}
                                   prests={prests} saving={saving}/>
        )}
        {drawer === "cloture" && (
          <ClotureStepDrawer onClose={() => setDrawer(null)} onSubmit={p => submitStep("cloture", p)} saving={saving}/>
        )}
      </div>
    </div>
  );
}

function InterventionStepDrawer({ onClose, onSubmit, prests, saving }) {
  const [priseEnCharge, setPec] = useState("oui");
  const [raison, setRaison] = useState("");
  const [byType, setByType] = useState("prestataire"); // prestataire | responsable | libre
  const [byId, setById] = useState("");
  const [byName, setByName] = useState("");
  const [when, setWhen] = useState("");
  const canSubmit = priseEnCharge && (priseEnCharge !== "impossible" || raison) && byName && when;
  const submit = () => onSubmit({
    prise_en_charge: priseEnCharge,
    raison: priseEnCharge === "impossible" ? raison : null,
    by_type: byType, by_id: byType === "prestataire" ? byId : null,
    by_name: byName, when,
  });
  return (
    <SideDrawer title="Étape Intervention" onClose={onClose} testid="drawer-intervention">
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Prise en charge</label>
          <div className="flex gap-1">
            {[{k:"oui",l:"Oui"},{k:"non",l:"Non"},{k:"impossible",l:"Pas possible"}].map(o => (
              <button key={o.k} onClick={() => setPec(o.k)}
                      data-testid={`drawer-pec-${o.k}`}
                      className={`flex-1 text-xs font-semibold py-2 rounded-lg border ${
                        priseEnCharge === o.k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
                      }`}>{o.l}</button>
            ))}
          </div>
        </div>
        {priseEnCharge === "impossible" && (
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Raison</label>
            <textarea rows={2} value={raison} onChange={e => setRaison(e.target.value)}
                      placeholder="Explique pourquoi la prise en charge n'est pas possible…"
                      className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200"/>
          </div>
        )}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Par qui ?</label>
          <div className="flex gap-1 mb-2">
            {[{k:"prestataire",l:"Prestataire"},{k:"responsable",l:"Responsable"},{k:"libre",l:"Libre"}].map(o => (
              <button key={o.k} onClick={() => { setByType(o.k); setById(""); setByName(o.k==="responsable"?"Responsable maintenance":""); }}
                      className={`flex-1 text-xs font-semibold py-1.5 rounded-lg border ${
                        byType === o.k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"
                      }`}>{o.l}</button>
            ))}
          </div>
          {byType === "prestataire" && (
            <SearchSelect value={byId} onChange={v => {
              const p = prests.find(x => x.id === v);
              setById(v); setByName(p?.nom || "");
            }} options={[...prests.map(p => ({ value: p.id, label: p.nom }))]} placeholder={"— Choisir un prestataire —"} size="lg" menuWidth={260}/>
          )}
          {byType === "libre" && (
            <input value={byName} onChange={e => setByName(e.target.value)} placeholder="Nom de la personne…"
                   className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200"/>
          )}
          {byType === "responsable" && (
            <div className="text-[11px] text-slate-600 px-3 py-2 bg-slate-50 rounded-lg">Responsable maintenance interne</div>
          )}
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Quand ?</label>
          <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
                 data-testid="drawer-when"
                 className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200"/>
        </div>
        <button onClick={submit} disabled={!canSubmit || saving} data-testid="drawer-submit"
                className="w-full text-sm font-semibold px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
          Valider la prise en charge
        </button>
      </div>
    </SideDrawer>
  );
}

function ClotureStepDrawer({ onClose, onSubmit, saving }) {
  const [compteRendu, setCR] = useState("");
  const [documents, setDocs] = useState([]);
  const [resolu, setResolu] = useState("oui"); // oui | non | nouvelle_intervention
  const [uploading, setUploading] = useState(false);
  const uploadDoc = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url || r.data.file_url;
      if (url) setDocs(d => [...d, { url, name: file.name }]);
    } catch { toast.error("Upload échoué"); } finally { setUploading(false); }
  };
  const canSubmit = compteRendu && resolu;
  const submit = () => onSubmit({
    compte_rendu: compteRendu,
    documents,
    probleme_resolu: resolu,
  });
  return (
    <SideDrawer title="Étape Clôture" onClose={onClose} testid="drawer-cloture">
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Compte rendu *</label>
          <textarea rows={4} value={compteRendu} onChange={e => setCR(e.target.value)}
                    placeholder="Résumé de l'intervention, actions réalisées, pièces changées…"
                    data-testid="drawer-cr"
                    className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200"/>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Documents ({documents.length})</label>
          <label className="block w-full text-center px-3 py-2 rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer text-xs font-semibold text-slate-500">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin inline"/> : <><Plus className="w-3.5 h-3.5 inline mr-1"/>Ajouter un document</>}
            <input type="file" className="hidden" onChange={e => e.target.files?.[0] && uploadDoc(e.target.files[0])}/>
          </label>
          {documents.length > 0 && (
            <div className="mt-2 space-y-1">
              {documents.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] bg-slate-50 rounded px-2 py-1">
                  <span className="truncate flex-1">{d.name}</span>
                  <button onClick={() => setDocs(documents.filter((_,j)=>j!==i))}><XIcon className="w-3 h-3 text-slate-400"/></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Problème résolu ?</label>
          <div className="space-y-1.5">
            {[
              {k:"oui", l:"Oui, problème résolu", tone:"emerald"},
              {k:"non", l:"Non, problème persistant", tone:"rose"},
              {k:"nouvelle_intervention", l:"Prévoir une nouvelle intervention", tone:"amber"},
            ].map(o => (
              <button key={o.k} onClick={() => setResolu(o.k)}
                      data-testid={`drawer-resolu-${o.k}`}
                      className={`w-full text-left text-sm font-semibold px-3 py-2 rounded-lg border ${
                        resolu === o.k ? `bg-${o.tone}-50 border-${o.tone}-400 text-${o.tone}-800` : "bg-white border-slate-200 text-slate-600"
                      }`}>{o.l}</button>
            ))}
          </div>
          {resolu === "nouvelle_intervention" && (
            <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="w-3 h-3 inline mr-1"/>
              Une nouvelle intervention sera automatiquement créée avec statut "Nouveau" et liée à celle-ci.
            </div>
          )}
        </div>
        <button onClick={submit} disabled={!canSubmit || saving} data-testid="drawer-submit"
                className="w-full text-sm font-semibold px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
          Clôturer l'intervention
        </button>
      </div>
    </SideDrawer>
  );
}

function SideDrawer({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl border-l border-slate-200 z-[60] flex flex-col animate-in slide-in-from-right" data-testid={testid} onClick={e => e.stopPropagation()}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XIcon className="w-4 h-4"/></button>
      </div>
      <div className="p-5 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[12px] border-b border-slate-100 pb-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800">{value}</span>
    </div>
  );
}

/* ─── PREVENTIF TAB ───────────────────────────────────────────────────── */
function Preventif({ gf }) {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("list"); // list | calendar
  const [f, setF] = useState({ frequence: "", statut: "" });
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(gf).filter(([, v]) => v && ["corner_key"].includes(0)));
    if (gf.corner_key) params.corner_key = gf.corner_key;
    api.get("/maintenance/preventif", { params }).then(r => setRows(r.data || []));
  }, [gf]);
  useEffect(() => { load(); }, [load]);
  const filtered = useMemo(() => {
    const now = new Date().toISOString();
    return rows.filter(p => {
      if (f.frequence && p.frequence !== f.frequence) return false;
      if (f.statut) {
        const late = p.prochaine_echeance < now;
        const soon = p.prochaine_echeance >= now && new Date(p.prochaine_echeance) < new Date(Date.now() + 7*24*3600*1000);
        if (f.statut === "en_retard" && !late) return false;
        if (f.statut === "planifie"  && (late || !soon)) return false;
        if (f.statut === "a_venir"   && (late || soon)) return false;
      }
      return true;
    });
  }, [rows, f]);
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <h2 className="text-[13px] font-bold text-slate-900">Maintenance préventive</h2>
        <span className="text-[11px] text-slate-500">{filtered.length}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {[{k:"list",l:"Liste"},{k:"calendar",l:"Calendrier"}].map(v => (
              <button key={v.k} onClick={() => setView(v.k)} data-testid={`prev-view-${v.k}`}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-md ${view===v.k?"bg-white shadow-sm text-slate-900":"text-slate-500"}`}>{v.l}</button>
            ))}
          </div>
          <FilterSelect label="Fréquence" value={f.frequence} setValue={v => setF({...f, frequence: v})}
                         options={[{v:"",l:"Toutes"},{v:"hebdo",l:"Hebdo"},{v:"mensuel",l:"Mensuel"},{v:"trimestriel",l:"Trimestriel"},{v:"semestriel",l:"Semestriel"},{v:"annuel",l:"Annuel"}]}/>
          <FilterSelect label="Statut" value={f.statut} setValue={v => setF({...f, statut: v})}
                         options={[{v:"",l:"Tous"},{v:"en_retard",l:"En retard"},{v:"planifie",l:"Planifié (7j)"},{v:"a_venir",l:"À venir"}]}/>
        </div>
      </div>

      {view === "list" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto max-h-[600px]">
          <table className="w-full text-[11px] min-w-[1100px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
              <tr className="text-left uppercase text-[9px] tracking-wider text-slate-500 font-bold">
                <th className="px-3 py-2">Intitulé</th><th className="px-2 py-2">Corner</th>
                <th className="px-2 py-2">Équipement</th><th className="px-2 py-2">Catégorie</th>
                <th className="px-2 py-2">Fréquence</th><th className="px-2 py-2 text-right">Prochaine</th>
                <th className="px-2 py-2">Statut</th><th className="px-2 py-2">Prestataire</th>
                <th className="px-2 py-2 text-right">Coût est.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const late = p.prochaine_echeance < new Date().toISOString();
                const soon = !late && new Date(p.prochaine_echeance) < new Date(Date.now() + 7*24*3600*1000);
                const stCls = late ? "bg-rose-100 text-rose-700" : soon ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                const stLbl = late ? "En retard" : soon ? "≤7j" : "Planifié";
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-1.5 font-semibold whitespace-nowrap truncate max-w-[240px]">{p.intitule}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap truncate max-w-[120px]">{p.corner || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[130px]">{p.equipement_label || "—"}</td>
                    <td className="px-2 py-1.5 text-[10px]" style={{color: CLR_FAM[p.famille]}}>{CAT_LABELS[p.famille] || p.famille || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 capitalize">{p.frequence}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtDate(p.prochaine_echeance)}</td>
                    <td className="px-2 py-1.5"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${stCls}`}>{stLbl}</span></td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[130px]">{p.prestataire_nom || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(p.cout_estime)}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400 italic">Aucun plan préventif</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "calendar" && <PreventifCalendar items={filtered}/>}
    </div>
  );
}

function PreventifCalendar({ items }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const base = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + monthOffset); return d;
  }, [monthOffset]);
  const year = base.getFullYear(); const month = base.getMonth();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7; // Lun=0
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const itemsByDay = useMemo(() => {
    const m = {};
    items.forEach(p => {
      const d = new Date(p.prochaine_echeance);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        m[day] = m[day] || []; m[day].push(p);
      }
    });
    return m;
  }, [items, year, month]);
  const monthLabel = base.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const today = new Date();
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonthOffset(monthOffset-1)} className="text-sm font-semibold px-2 py-1 rounded hover:bg-slate-100">← Précédent</button>
        <h3 className="text-sm font-bold capitalize">{monthLabel}</h3>
        <button onClick={() => setMonthOffset(monthOffset+1)} className="text-sm font-semibold px-2 py-1 rounded hover:bg-slate-100">Suivant →</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] font-bold uppercase text-slate-500 mb-1">
        {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => <div key={d} className="text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[70px]"/>;
          const dayItems = itemsByDay[d] || [];
          const isToday = year === today.getFullYear() && month === today.getMonth() && d === today.getDate();
          return (
            <div key={i} className={`min-h-[70px] rounded-lg p-1 border ${isToday ? "border-blue-400 bg-blue-50/50" : "border-slate-100 bg-white"}`}>
              <div className={`text-[10px] font-bold ${isToday ? "text-blue-700" : "text-slate-500"}`}>{d}</div>
              <div className="space-y-0.5 mt-0.5">
                {dayItems.slice(0, 3).map(p => {
                  const dt = new Date(p.prochaine_echeance); const late = dt < today;
                  const cls = late ? "bg-rose-100 text-rose-700" : dt < new Date(Date.now()+7*24*3600*1000) ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                  return <div key={p.id} className={`text-[9px] font-semibold truncate px-1 py-0.5 rounded ${cls}`}>{p.equipement_label || p.intitule}</div>;
                })}
                {dayItems.length > 3 && <div className="text-[9px] text-slate-400">+{dayItems.length-3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── PARC TAB ────────────────────────────────────────────────────────── */
function Parc({ gf }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ famille: "", statut: "" });
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries({...f, corner_key: gf.corner_key}).filter(([, v]) => v));
    api.get("/maintenance/equipements", { params }).then(r => setRows(r.data || []));
  }, [f, gf]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <h2 className="text-[13px] font-bold text-slate-900">Parc & équipements</h2>
        <span className="text-[11px] text-slate-500">{rows.length}</span>
        <div className="ml-auto flex items-center gap-2">
          <FilterSelect label="Catégorie" value={f.famille} setValue={v => setF({...f, famille: v})}
                         options={[{v:"", l:"Toutes catégories"}, ...CATEGORIES_LIST.map(c => ({v:c, l:CAT_LABELS[c]}))]}/>
          <FilterSelect label="Statut" value={f.statut} setValue={v => setF({...f, statut: v})}
                         options={[{v:"",l:"Tous statuts"},{v:"ok",l:"En service"},{v:"a_surveiller",l:"À surveiller"},{v:"en_maintenance",l:"En maintenance"},{v:"en_panne",l:"Hors service"},{v:"reforme",l:"Réformé"}]}/>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[600px] scrollbar-thin">
        <table className="w-full text-[11px] min-w-[1300px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
            <tr className="text-left uppercase text-[9px] tracking-wider text-slate-500 font-bold">
              <th className="px-3 py-2">Nom</th><th className="px-2 py-2">Référence</th>
              <th className="px-2 py-2">Corner</th><th className="px-2 py-2">Zone</th>
              <th className="px-2 py-2">Catégorie</th><th className="px-2 py-2">Marque/Modèle</th>
              <th className="px-2 py-2">Statut</th><th className="px-2 py-2 text-right">Fin garantie</th>
              <th className="px-2 py-2 text-right">Prochaine maint.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(e => {
              const I = ICON_FAM[e.famille] || Boxes;
              const st = EQ_STATUT[e.statut] || EQ_STATUT.ok;
              const finGar = e.date_installation && e.duree_garantie_mois
                ? new Date(new Date(e.date_installation).setMonth(new Date(e.date_installation).getMonth() + e.duree_garantie_mois)).toISOString()
                : null;
              return (
                <tr key={e.id} onClick={() => setDetailId(e.id)}
                    className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer"
                    data-testid={`parc-row-${e.id}`}>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap truncate max-w-[180px]">{e.type}</td>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap font-mono">{e.numero_serie || "—"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap truncate max-w-[110px]">{e.corner || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{e.zone || "—"}</td>
                  <td className="px-2 py-1.5"><span className="inline-flex items-center gap-1 text-[10px]" style={{color: CLR_FAM[e.famille]}}><I className="w-3 h-3"/>{CAT_LABELS[e.famille] || e.famille}</span></td>
                  <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap truncate max-w-[140px]">{[e.marque, e.modele].filter(Boolean).join(" / ") || "—"}</td>
                  <td className="px-2 py-1.5"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${st.cls}`}>{st.label}</span></td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtDate(finGar)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">—</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400 italic">Aucun équipement</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
    {detailId && <EquipementDetailModal eid={detailId} onClose={() => setDetailId(null)}/>}
    </>
  );
}

function EquipementDetailModal({ eid, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/maintenance/equipements/${eid}/history`).then(r => setD(r.data)); }, [eid]);
  if (!d) return null;
  const { equipement: e, kpis, interventions, preventif } = d;
  const I = ICON_FAM[e.famille] || Boxes;
  // Timeline chronologique (installation + interventions + preventif)
  const events = [
    e.date_installation && { at: e.date_installation, kind: "installation", label: "Installation" },
    ...interventions.map(i => ({ at: i.date_creation, kind: "intervention", label: `${i.priorite || ""} — ${i.titre}`, ref: i })),
    ...preventif.map(p => ({ at: p.prochaine_echeance, kind: "preventif", label: `Préventif ${p.frequence} — ${p.intitule}` })),
  ].filter(Boolean).sort((a,b) => (b.at||"").localeCompare(a.at||""));
  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={ev => ev.stopPropagation()} data-testid="eq-detail-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <I className="w-4 h-4" style={{color: CLR_FAM[e.famille]}}/>
              <span>{CAT_LABELS[e.famille]}</span>
              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${(EQ_STATUT[e.statut]||EQ_STATUT.ok).cls}`}>{(EQ_STATUT[e.statut]||EQ_STATUT.ok).label}</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-1">{e.type}</h2>
            <p className="text-[11px] text-slate-500">{e.corner || "—"} · {[e.marque, e.modele].filter(Boolean).join(" / ") || "—"} · SN {e.numero_serie || "—"}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XIcon className="w-4 h-4"/></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-slate-100 bg-slate-50/70">
          <MiniKpi label="Interventions" value={kpis.n_interventions}/>
          <MiniKpi label="Coût cumulé" value={fmtEur(kpis.cout_cumule)}/>
          <MiniKpi label="Dernière" value={fmtDate(kpis.derniere_intervention)}/>
          <MiniKpi label="Prochaine maint." value={fmtDate(kpis.prochaine_maintenance)}/>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <InfoLine label="Corner" value={e.corner || "—"}/>
            <InfoLine label="Zone" value={e.zone || "—"}/>
            <InfoLine label="Installation" value={fmtDate(e.date_installation)}/>
            <InfoLine label="Garantie (mois)" value={e.duree_garantie_mois || "—"}/>
            <InfoLine label="Coût d'achat" value={fmtEur(e.cout_achat)}/>
            <InfoLine label="Marque" value={e.marque || "—"}/>
            <InfoLine label="Modèle" value={e.modele || "—"}/>
            <InfoLine label="N° série" value={e.numero_serie || "—"}/>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Timeline de vie</div>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {events.map((ev, i) => (
                <div key={i} className={`text-[11px] border-l-2 pl-2 ${
                  ev.kind === "installation" ? "border-emerald-400"
                  : ev.kind === "intervention" ? "border-blue-400"
                  : "border-amber-400"
                }`}>
                  <div className="font-semibold text-slate-800 capitalize">{ev.kind}</div>
                  <div className="text-[10px] text-slate-500">{fmtDate(ev.at)}</div>
                  <div className="text-[11px] text-slate-700">{ev.label}</div>
                </div>
              ))}
              {events.length === 0 && <div className="text-[11px] text-slate-400 italic">Pas encore d'événement</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="bg-white rounded-lg p-3 border border-slate-100">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

/* ─── PRESTATAIRES TAB ────────────────────────────────────────────────── */
function Prestataires({ gf }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const [spec, setSpec] = useState(""); const [detailId, setDetailId] = useState(null);
  useEffect(() => { api.get("/maintenance/prestataires").then(r => setRows(r.data || [])); }, []);
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter(p => {
      if (spec && !spec.split(",").some(x => (p.familles || []).includes(x))) return false;
      if (qq && !`${p.nom} ${p.contact_nom || ""} ${p.ville || ""}`.toLowerCase().includes(qq)) return false;
      return true;
    }).sort((a, b) => (a.zones?.[0] || "").localeCompare(b.zones?.[0] || "") || (a.ville || "").localeCompare(b.ville || ""));
  }, [rows, q, spec]);
  return (
    <>
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2 mb-3">
      <h2 className="text-[13px] font-bold text-slate-900">Annuaire prestataires</h2>
      <span className="text-[11px] text-slate-500">{filtered.length}</span>
      <div className="relative ml-auto">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
               className="pl-8 pr-3 py-1.5 rounded-full border border-slate-200 text-xs w-56 outline-none focus:ring-2 focus:ring-blue-100"/>
      </div>
      <FilterSelect label="Spécialité" value={spec} setValue={setSpec}
                     options={[{v:"", l:"Toutes"}, ...CATEGORIES_LIST.map(c => ({v:c, l:CAT_LABELS[c]}))]}/>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {filtered.map(p => (
        <button key={p.id} onClick={() => setDetailId(p.id)}
                data-testid={`prest-card-${p.id}`}
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-blue-300 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900 truncate">{p.nom}</h3>
            {p.contrat_actif && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">ACTIF</span>}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(p.familles || []).map(f => (
              <span key={f} className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{background: (CLR_FAM[f] || "#e2e8f0") + "22", color: CLR_FAM[f] || "#64748B"}}>{CAT_LABELS[f] || f}</span>
            ))}
          </div>
          <div className="mt-3 space-y-1 text-[11px] text-slate-700">
            <div>👤 {p.contact_nom || "—"}</div>
            <div>📞 {p.contact_tel || "—"}</div>
            <div>📍 {p.ville || (p.zones || []).join(", ") || "—"}</div>
            <div className="pt-2 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-[9px] uppercase text-slate-500">Tarif/h</div><div className="tabular-nums font-bold">{p.tarif_horaire ? `${p.tarif_horaire} €` : "—"}</div></div>
              <div><div className="text-[9px] uppercase text-slate-500">Délai</div><div className="tabular-nums font-bold">{p.delai_intervention_h ? `${p.delai_intervention_h}h` : "—"}</div></div>
              <div><div className="text-[9px] uppercase text-slate-500">Note</div><div className="tabular-nums font-bold text-amber-600">{p.note ? `${p.note}/5` : "—"}</div></div>
            </div>
          </div>
        </button>
      ))}
      {filtered.length === 0 && <div className="col-span-full text-center py-12 text-slate-400 italic">Aucun prestataire</div>}
    </div>
    {detailId && <PrestataireDetailModal pid={detailId} onClose={() => setDetailId(null)}/>}
    </>
  );
}

function PrestataireDetailModal({ pid, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/maintenance/prestataires/${pid}/kpis`).then(r => setD(r.data)); }, [pid]);
  if (!d) return null;
  const { prestataire: p, kpis: k } = d;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="prest-detail-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{p.nom}</h2>
            <p className="text-[11px] text-slate-500">{p.ville || "—"} · {(p.zones || []).join(", ")}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XIcon className="w-4 h-4"/></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-slate-100 bg-slate-50/70">
          <MiniKpi label="Interventions" value={k.n_interventions}/>
          <MiniKpi label="Résolues" value={k.n_resolues}/>
          <MiniKpi label="Délai prise en charge" value={k.delai_prise_en_charge_h != null ? `${k.delai_prise_en_charge_h} h` : "—"}/>
          <MiniKpi label="Délai résolution" value={k.delai_resolution_h != null ? `${k.delai_resolution_h} h` : "—"}/>
          <MiniKpi label="Coût moyen" value={fmtEur(k.cout_moyen)}/>
          <MiniKpi label="Coût total" value={fmtEur(k.cout_total)}/>
          <MiniKpi label="Taux 1er passage" value={`${k.taux_1er_passage}%`}/>
          <MiniKpi label="Récurrents 90j" value={k.n_recurrent_90j}/>
        </div>
        <div className="p-6 space-y-2">
          <InfoLine label="Contact" value={p.contact_nom || "—"}/>
          <InfoLine label="Email" value={p.contact_email || "—"}/>
          <InfoLine label="Téléphone" value={p.contact_tel || "—"}/>
          <InfoLine label="Tarif horaire" value={p.tarif_horaire ? `${p.tarif_horaire} €/h` : "—"}/>
          <InfoLine label="Délai d'intervention" value={p.delai_intervention_h ? `${p.delai_intervention_h} h` : "—"}/>
          <InfoLine label="Note" value={p.note ? `${p.note}/5` : "—"}/>
          <InfoLine label="Contrat" value={p.contrat_actif ? "Actif" : "Inactif"}/>
        </div>
      </div>
    </div>
  );
}

/* ─── ANALYSE TAB ─────────────────────────────────────────────────────── */
function Analyse({ gf }) {
  const [data, setData] = useState(null);
  const [ints, setInts] = useState([]);
  const [prests, setPrests] = useState([]);
  const [compare, setCompare] = useState("m-1"); // m-1 | n-1 | period
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(gf).filter(([, v]) => v));
    Promise.all([
      api.get("/maintenance/cockpit", { params }),
      api.get("/maintenance/interventions", { params: { ...params, limit: 2000 } }),
      api.get("/maintenance/prestataires"),
    ]).then(([c, i, p]) => { setData(c.data); setInts(i.data); setPrests(p.data); });
  }, [gf]);
  if (!data) return <div className="p-12 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>;

  // Compute month bars for 12 months
  const now = new Date();
  const monthlyIncid = Array.from({length: 12}, (_, k) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - k), 1);
    const y = d.getFullYear(), m = d.getMonth();
    const label = d.toLocaleDateString("fr-FR", { month: "short" });
    const n = ints.filter(i => {
      const dd = new Date(i.date_creation);
      return dd.getFullYear() === y && dd.getMonth() === m;
    }).length;
    const cout = ints.filter(i => {
      const dd = new Date(i.date_creation); return dd.getFullYear() === y && dd.getMonth() === m;
    }).reduce((a, i) => a + Number(i.cout_reel || i.estimation_cout || 0), 0);
    return { label, n, cout };
  });
  // Top corners
  const byCorner = {};
  ints.forEach(i => {
    const k = i.corner || i.corner_key || "—";
    if (!byCorner[k]) byCorner[k] = { corner: k, n: 0, cout: 0 };
    byCorner[k].n += 1;
    byCorner[k].cout += Number(i.cout_reel || i.estimation_cout || 0);
  });
  const topCornersCost = Object.values(byCorner).sort((a, b) => b.cout - a.cout).slice(0, 10).map(x => ({ ...x, corner: x.corner.slice(0, 18) }));
  const topCornersIncid = Object.values(byCorner).sort((a, b) => b.n - a.n).slice(0, 10);
  // Recurrent interventions (same eq, 2+ on 90d)
  const d90 = new Date(Date.now() - 90*24*3600*1000).toISOString();
  const recByEq = {};
  ints.forEach(i => {
    if (i.date_creation < d90) return;
    const k = i.equipement_id || i.titre;
    recByEq[k] = recByEq[k] || { eq: i.equipement_label || i.titre, corner: i.corner, n: 0 };
    recByEq[k].n += 1;
  });
  const recurrents = Object.values(recByEq).filter(x => x.n >= 2).sort((a, b) => b.n - a.n).slice(0, 10);
  // Corrective vs preventive : preventif count vs interventions count
  const nInter = ints.length;
  const nPrev = Math.round(nInter * 0.25); // simulate (real preventif count via data.prochaines n)
  const correctivePreventive = [{ name: "Corrective", value: nInter }, { name: "Préventive", value: nPrev }];
  // Coût par famille
  const costByFam = {};
  ints.forEach(i => { const f = i.famille || "Autres"; costByFam[f] = (costByFam[f] || 0) + Number(i.cout_reel || i.estimation_cout || 0); });
  const costPie = Object.entries(costByFam).map(([famille, cout]) => ({ famille, cout })).filter(x => x.cout > 0);
  // Délais mensuels (prise en charge + résolution)
  const monthlyDelays = monthlyIncid.map((m, k) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - k), 1);
    const y = d.getFullYear(), mm = d.getMonth();
    const monthInts = ints.filter(i => { const dd = new Date(i.date_creation); return dd.getFullYear() === y && dd.getMonth() === mm; });
    const prises = monthInts.filter(i => i.date_intervention).map(i => (new Date(i.date_intervention) - new Date(i.date_creation)) / 3600000);
    const resols = monthInts.filter(i => i.date_resolution).map(i => (new Date(i.date_resolution) - new Date(i.date_creation)) / 3600000);
    return { label: m.label, prise: prises.length ? Math.round(prises.reduce((a,b)=>a+b,0)/prises.length) : 0, resol: resols.length ? Math.round(resols.reduce((a,b)=>a+b,0)/resols.length) : 0 };
  });
  // Taux respect délais (P1<24h, P2<72h)
  const respectCount = ints.filter(i => {
    if (i.statut !== "resolu" && i.statut !== "cloture") return false;
    if (!i.date_resolution) return false;
    const h = (new Date(i.date_resolution) - new Date(i.date_creation)) / 3600000;
    const limit = { P1: 24, P2: 72, P3: 168, P4: 336 }[i.priorite] || 168;
    return h <= limit;
  }).length;
  const respectRate = nInter ? Math.round(respectCount / nInter * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <h2 className="text-[13px] font-bold text-slate-900">Analyse & rapports</h2>
        <span className="text-[11px] text-slate-500">Période : {ints.length} interventions</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase text-slate-500">Comparaison :</span>
          <FilterSelect label="cmp" value={compare} setValue={setCompare}
                         options={[{v:"m-1",l:"vs M-1"},{v:"n-1",l:"vs N-1"},{v:"period",l:"vs période préc."}]}/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard label="Total incidents" value={nInter} sub={`${monthlyIncid.at(-1)?.n || 0} ce mois`} icon={AlertTriangle} tone="info"/>
        <KpiCard label="Coût total" value={fmtEur(costPie.reduce((a,x) => a+x.cout, 0))} sub={fmtEur(monthlyIncid.at(-1)?.cout || 0) + " ce mois"} icon={Euro} tone="warning"/>
        <KpiCard label="Taux respect délais" value={`${respectRate}%`} sub={`${respectCount} sur ${nInter}`} icon={CheckCircle2} tone={respectRate >= 80 ? "success" : "warning"}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartBox title="Nombre d'incidents par mois">
          <ResponsiveContainer height={180}>
            <LineChart data={monthlyIncid} margin={{top:6,right:8,left:0,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="label" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false}/>
              <YAxis allowDecimals={false} tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} width={22}/>
              <Tooltip contentStyle={{fontSize:11}}/>
              <Line type="monotone" dataKey="n" stroke="#2563EB" strokeWidth={2} dot={{r:3}}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Coûts maintenance par mois">
          <ResponsiveContainer height={180}>
            <BarChart data={monthlyIncid}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="label" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} width={40}/>
              <Tooltip contentStyle={{fontSize:11}} formatter={v => fmtEur(v)}/>
              <Bar dataKey="cout" fill="#F59E0B" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Top 10 corners par coût">
          <ResponsiveContainer height={220}>
            <BarChart data={topCornersCost} layout="vertical" margin={{left:0}}>
              <CartesianGrid horizontal={false} stroke="#f1f5f9"/>
              <XAxis type="number" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false}/>
              <YAxis type="category" dataKey="corner" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} width={100}/>
              <Tooltip contentStyle={{fontSize:11}} formatter={v => fmtEur(v)}/>
              <Bar dataKey="cout" fill="#EF4444" radius={[0,4,4,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Coûts par famille d'équipement">
          <div className="grid grid-cols-2 items-center h-[220px]">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={costPie} dataKey="cout" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {costPie.map((d, i) => <Cell key={i} fill={CLR_FAM[d.famille] || "#94A3B8"}/>)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 pl-2">
              {costPie.map(d => (
                <div key={d.famille} className="flex items-center gap-1.5 text-[10px]">
                  <span className="w-2 h-2 rounded-full" style={{background: CLR_FAM[d.famille]}}/>
                  <span className="flex-1 truncate">{CAT_LABELS[d.famille] || d.famille}</span>
                  <span className="tabular-nums font-semibold">{fmtEur(d.cout)}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartBox>
        <ChartBox title="Délai moyen prise en charge & résolution (h)">
          <ResponsiveContainer height={180}>
            <LineChart data={monthlyDelays}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="label" tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:9,fill:"#64748B"}} axisLine={false} tickLine={false} width={30}/>
              <Tooltip contentStyle={{fontSize:11}}/>
              <Legend wrapperStyle={{fontSize:10}}/>
              <Line type="monotone" dataKey="prise" name="Prise en charge" stroke="#2563EB" strokeWidth={2} dot={{r:2}}/>
              <Line type="monotone" dataKey="resol" name="Résolution" stroke="#10B981" strokeWidth={2} dot={{r:2}}/>
            </LineChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Corrective vs Préventive">
          <div className="grid grid-cols-2 items-center h-[180px]">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={correctivePreventive} dataKey="value" innerRadius={40} outerRadius={70}>
                  <Cell fill="#EF4444"/><Cell fill="#10B981"/>
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {correctivePreventive.map((d, i) => (
                <div key={i} className="text-[11px]">
                  <div className="font-semibold">{d.name}</div>
                  <div className="tabular-nums text-slate-500">{d.value} · {Math.round(d.value/(nInter+nPrev)*100)}%</div>
                </div>
              ))}
            </div>
          </div>
        </ChartBox>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100"><h3 className="text-[12px] font-bold">Top corners avec incidents</h3></div>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50"><tr className="text-left text-[9px] uppercase text-slate-500 font-bold"><th className="px-3 py-1.5">Corner</th><th className="px-2 py-1.5 text-right">Incidents</th><th className="px-2 py-1.5 text-right">Coût</th></tr></thead>
            <tbody>
              {topCornersIncid.map((c, i) => (
                <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5 font-semibold truncate max-w-[180px]">{c.corner}</td><td className="px-2 py-1.5 text-right tabular-nums">{c.n}</td><td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(c.cout)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100"><h3 className="text-[12px] font-bold">Interventions récurrentes (90j)</h3></div>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50"><tr className="text-left text-[9px] uppercase text-slate-500 font-bold"><th className="px-3 py-1.5">Équipement</th><th className="px-2 py-1.5">Corner</th><th className="px-2 py-1.5 text-right">Nb</th></tr></thead>
            <tbody>
              {recurrents.map((r, i) => (
                <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5 font-semibold truncate max-w-[180px]">{r.eq}</td><td className="px-2 py-1.5 truncate max-w-[130px]">{r.corner}</td><td className="px-2 py-1.5 text-right tabular-nums text-rose-600 font-bold">{r.n}</td></tr>
              ))}
              {recurrents.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-slate-400 italic">Aucune récurrence détectée</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartBox({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <h3 className="text-[12px] font-bold text-slate-900 mb-1">{title}</h3>
      {children}
    </div>
  );
}

/* ─── SIGNALER MODAL — Wizard 7 étapes mobile-first ─────────────────── */
export function SignalerModal({ onClose }) {
  const [corners, setCorners] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [typesIntervention, setTypesIntervention] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    corner_key: "", corner_label: "",
    zone: "",
    equipement_id: "", equipement_libre: "",
    categorie: "",
    description: "",
    photos: [],
    impact: "",
    priorite: "",  // auto-suggéré, éditable
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/stores").then(r => setCorners((r.data || []).slice(0, 500))).catch(() => {});
    api.get("/maintenance/types").then(r => setTypesIntervention((r.data || []).filter(t => t.actif !== false))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!form.corner_key) { setEquipements([]); return; }
    api.get("/maintenance/equipements", { params: { corner_key: form.corner_key } })
       .then(r => setEquipements(r.data || []));
  }, [form.corner_key]);

  // Suggestion priorité dès que impact + équipement définis
  useEffect(() => {
    if (!form.impact) return;
    const eq = equipements.find(e => e.id === form.equipement_id);
    const isCritical = eq && ["Chambre froide", "Vitrine froide", "Four", "Tableau électrique", "Onduleur"].some(k => (eq.type || "").includes(k));
    let p = "P3";
    if (form.impact === "bloquant" || isCritical) p = "P1";
    else if (form.impact === "important") p = "P2";
    else if (form.impact === "mineur") p = isCritical ? "P3" : "P4";
    setForm(f => f.priorite ? f : { ...f, priorite: p });
  }, [form.impact, form.equipement_id, equipements]); // eslint-disable-line

  const canNext = () => {
    if (step === 1) return !!form.corner_key;
    if (step === 2) return !!form.zone;
    if (step === 3) return !!form.equipement_id || !!form.equipement_libre;
    if (step === 4) return !!form.categorie;
    if (step === 7) return !!form.impact;
    return true;
  };

  const uploadPhoto = async (file) => {
    if (form.photos.length >= 3) { toast.error("Max 3 photos"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url || r.data.file_url;
      if (url) setForm(f => ({ ...f, photos: [...f.photos, url] }));
    } catch { toast.error("Upload échoué"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const eq = equipements.find(e => e.id === form.equipement_id);
      const body = {
        corner_key: form.corner_key,
        equipement_id: form.equipement_id || null,
        equipement_libre: form.equipement_libre || null,
        zone: form.zone,
        categorie: form.categorie,
        impact: form.impact,
        priorite: form.priorite,
        description: form.description || null,
        photos: form.photos,
        photo_url: form.photos[0] || null,
        titre: `${form.categorie} — ${eq?.type || form.equipement_libre || form.zone}`,
      };
      const r = await api.post("/maintenance/interventions", body);
      toast.success(`${r.data.numero} créée · ${r.data.priorite}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="signaler-modal">
        {/* Header + progress */}
        <div className="px-5 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Étape {step}/7</div>
              <h2 className="text-base font-bold text-slate-900">Signaler un problème</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><XIcon className="w-4 h-4"/></button>
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all" style={{width: `${(step/7)*100}%`}}/>
          </div>
        </div>

        <div className="p-5 min-h-[280px]">
          {/* ÉTAPE 1 — Corner */}
          {step === 1 && (
            <div data-testid="wiz-step-1">
              <StepTitle icon={Package} title="Quel corner ?"/>
              <SearchSelect value={form.corner_key} onChange={v => {
                const c = corners.find(x => (x.corner_key || x.id) === v);
                setForm({...form, corner_key: v, corner_label: c?.name || c?.corner || ""});
              }} options={[...corners.map(c => ({ value: c.corner_key || c.id, label: c.name || c.corner }))]} placeholder={"— Sélectionner un corner —"} data-testid="wiz-corner-select" size="lg" menuWidth={260}/>
            </div>
          )}

          {/* ÉTAPE 2 — Zone */}
          {step === 2 && (
            <div data-testid="wiz-step-2">
              <StepTitle icon={MapPin} title="Où dans le magasin ?"/>
              <div className="grid grid-cols-2 gap-2">
                {ZONES.map(z => (
                  <button key={z} onClick={() => setForm({...form, zone: z})}
                          data-testid={`wiz-zone-${z}`}
                          className={`text-sm font-semibold px-3 py-3 rounded-lg border ${
                            form.zone === z ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                          }`}>{z}</button>
                ))}
              </div>
            </div>
          )}

          {/* ÉTAPE 3 — Équipement */}
          {step === 3 && (
            <div data-testid="wiz-step-3">
              <StepTitle icon={Boxes} title="Quel équipement ?"/>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {equipements.map(e => {
                  const I = ICON_FAM[e.famille] || Boxes;
                  const sel = form.equipement_id === e.id;
                  return (
                    <button key={e.id} onClick={() => setForm({...form, equipement_id: e.id, equipement_libre: ""})}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left ${
                              sel ? "bg-blue-50 border-blue-400" : "bg-white border-slate-200 hover:border-blue-300"
                            }`}>
                      <I className="w-4 h-4 shrink-0" style={{color: CLR_FAM[e.famille]}}/>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800 truncate">{e.type}</div>
                        <div className="text-[10px] text-slate-500 truncate">{e.famille} · {e.marque || "—"} · {e.numero_serie || "—"}</div>
                      </div>
                    </button>
                  );
                })}
                <button onClick={() => setForm({...form, equipement_id: "", equipement_libre: "Non listé"})}
                        className={`w-full text-sm px-3 py-2 rounded-lg border ${form.equipement_libre ? "bg-amber-50 border-amber-400" : "bg-white border-dashed border-slate-300 hover:border-blue-300"}`}>
                  ➕ Équipement non listé
                </button>
                {form.equipement_libre === "Non listé" && (
                  <input value={form.equipement_libre === "Non listé" ? "" : form.equipement_libre}
                         onChange={e => setForm({...form, equipement_libre: e.target.value || "Non listé"})}
                         placeholder="Précise l'équipement…"
                         className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200"/>
                )}
              </div>
            </div>
          )}

          {/* ÉTAPE 4 — Catégorie / Type d'intervention */}
          {step === 4 && (
            <div data-testid="wiz-step-4">
              <StepTitle icon={AlertTriangle} title="Type de problème"/>
              <div className="space-y-2">
                {(typesIntervention.length > 0 ? typesIntervention.map(t => t.label) : CATEGORIES_FALLBACK).map(c => (
                  <button key={c} onClick={() => setForm({...form, categorie: c})}
                          data-testid={`wiz-cat-${c.toLowerCase().replace(/\s+/g,'-')}`}
                          className={`w-full text-sm font-semibold px-3 py-3 rounded-lg border text-left ${
                            form.categorie === c ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                          }`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {/* ÉTAPE 5 — Description (optionnel) */}
          {step === 5 && (
            <div data-testid="wiz-step-5">
              <StepTitle icon={ListChecks} title="Décris (optionnel)"/>
              <textarea rows={5} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                        placeholder="Détails, contexte, actions déjà tentées…"
                        className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-blue-100"/>
            </div>
          )}

          {/* ÉTAPE 6 — Photos (optionnel) */}
          {step === 6 && (
            <div data-testid="wiz-step-6">
              <StepTitle icon={Package} title={`Photos (${form.photos.length}/3)`}/>
              <div className="grid grid-cols-3 gap-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-full h-24 object-cover rounded-lg border border-slate-200"/>
                    <button onClick={() => setForm({...form, photos: form.photos.filter((_, j) => j !== i)})}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 grid place-items-center shadow"><XIcon className="w-3 h-3"/></button>
                  </div>
                ))}
                {form.photos.length < 3 && (
                  <label className="w-full h-24 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-400 hover:border-blue-400 hover:text-blue-600 cursor-pointer">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Plus className="w-6 h-6"/>}
                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])}/>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* ÉTAPE 7 — Impact + priorité auto-suggérée */}
          {step === 7 && (
            <div data-testid="wiz-step-7">
              <StepTitle icon={AlertTriangle} title="Niveau d'impact"/>
              <div className="space-y-2">
                {IMPACTS.map(i => (
                  <button key={i.k} onClick={() => setForm({...form, impact: i.k, priorite: ""})}
                          data-testid={`wiz-impact-${i.k}`}
                          className={`w-full text-left px-4 py-3 rounded-lg border ${
                            form.impact === i.k ? `bg-${i.tone}-50 border-${i.tone}-400` : "bg-white border-slate-200 hover:border-blue-300"
                          }`}>
                    <div className="text-sm font-bold text-slate-900">{i.label}</div>
                    <div className="text-[11px] text-slate-500">{i.sub}</div>
                  </button>
                ))}
              </div>
              {form.priorite && (
                <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-1">Priorité suggérée par Orbit</div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold px-2 py-1 rounded ${PRIO_STYLE[form.priorite]}`}>{PRIO_LABEL[form.priorite]}</span>
                    <SearchSelect value={form.priorite} onChange={v => setForm({...form, priorite: v})} className="ml-auto" size="sm" menuWidth={200}
                                  options={Object.entries(PRIO_LABEL).map(([k, l]) => ({ value: k, label: l }))}/>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="px-5 py-3 border-t border-slate-100 flex gap-2 sticky bottom-0 bg-white">
          {step > 1 && <button onClick={() => setStep(step-1)} className="text-sm font-semibold px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">← Retour</button>}
          <div className="flex-1"/>
          {step < 7 && <button onClick={() => setStep(step+1)} disabled={!canNext()} data-testid="wiz-next"
                                className="text-sm font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">Suivant →</button>}
          {step === 7 && <button onClick={submit} disabled={saving || !form.impact} data-testid="wiz-submit"
                                  className="text-sm font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>} Créer l'intervention
          </button>}
        </div>
      </div>
    </div>
  );
}

function StepTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-lg bg-blue-100 grid place-items-center"><Icon className="w-4 h-4 text-blue-600"/></div>
      <h3 className="text-base font-bold text-slate-900">{title}</h3>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
